/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import { toast } from 'sonner';
import {
    Search, MapPin, Building2, UserRound, ShieldCheck, Award,
    ArrowRight, X, Sparkles, Filter, Loader2, Mail,
} from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const SPECIALTIES = [
    'Premier achat', 'Investissement locatif', 'Vente patrimoniale',
    'Vente rapide', 'Bien atypique', 'Bien de prestige',
    'Bien commercial', 'Terrain', 'Neuf',
];
const PROPERTY_TYPES = [
    'Appartement', 'Maison', 'Terrain', 'Local commercial',
    'Immeuble', 'Bureau', 'Parking',
];
const LANGUAGES = ['Français', 'English', 'Español', 'العربية', 'Italiano'];

const PAGE_SIZE = 12;

// ─────────────────────────────────────────────────────────────────────────────
// Tracking analytics — fire & forget
// ─────────────────────────────────────────────────────────────────────────────
function track(event, payload = {}) {
    try {
        api.post('/public/search/analytics', { event, payload }).catch(() => {});
    } catch (_) { /* silent */ }
}

// ─────────────────────────────────────────────────────────────────────────────
// SEO head minimaliste (sans react-helmet-async)
// ─────────────────────────────────────────────────────────────────────────────
function SeoHead({ hasParams }) {
    useEffect(() => {
        const prevTitle = document.title;
        document.title = 'Trouver un agent immobilier · Réseau public Homly';

        const setMeta = (selector, attr, value) => {
            let el = document.head.querySelector(selector);
            const created = !el;
            if (!el) {
                el = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
                if (selector.includes('name=')) {
                    el.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
                } else if (selector.includes('rel=')) {
                    el.setAttribute('rel', selector.match(/rel="([^"]+)"/)[1]);
                }
                document.head.appendChild(el);
            }
            const prev = el.getAttribute(attr);
            el.setAttribute(attr, value);
            return () => {
                if (created) el.remove();
                else if (prev != null) el.setAttribute(attr, prev);
            };
        };

        const undos = [
            setMeta('meta[name="description"]', 'content',
                'Recherchez un agent ou une agence immobilière sur le réseau public Homly. Profils vérifiés, classement transparent, contact direct.'),
            setMeta('link[rel="canonical"]', 'href', 'https://homly.fr/recherche'),
            setMeta('meta[name="robots"]', 'content', hasParams ? 'noindex, follow' : 'index, follow'),
        ];
        return () => { document.title = prevTitle; undos.forEach(u => u && u()); };
    }, [hasParams]);
    return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────────────────────
function Hero({ query, city, onSubmit }) {
    const [q, setQ] = useState(query || '');
    const [c, setC] = useState(city || '');

    return (
        <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-[#681793]/20 via-transparent to-[#FF914D]/10 pointer-events-none" />
            <div className="relative max-w-5xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-10">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF914D]/30 bg-[#FF914D]/8 text-[10px] font-medium tracking-widest text-[#FF914D] uppercase"
                    style={MONO} data-testid="search-hero-eyebrow">
                    <Sparkles className="w-3 h-3" />
                    Réseau public Homly · pour les vendeurs
                </span>
                <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]" style={DISPLAY}>
                    Trouvez l'agent{' '}
                    <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                        qui correspond à votre projet.
                    </span>
                </h1>
                <p className="mt-5 text-base md:text-lg text-[#A19DAB] max-w-2xl leading-relaxed">
                    Une recherche transparente — pas de pub, pas de classement payant.
                    Vous comparez sur ce qui compte vraiment.
                </p>

                <form
                    onSubmit={(e) => { e.preventDefault(); onSubmit({ q: q.trim(), city: c.trim() }); }}
                    className="mt-8 grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-3 p-3 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm"
                    data-testid="search-hero-form"
                >
                    <div className="flex items-center gap-2 px-3 rounded-xl bg-white/[0.06] border border-white/10">
                        <Search className="w-4 h-4 text-[#A19DAB]" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Nom, agence, mot-clé…"
                            className="flex-1 bg-transparent py-3 text-sm text-white placeholder:text-[#6E6A7A] outline-none"
                            data-testid="search-input-q"
                        />
                    </div>
                    <div className="flex items-center gap-2 px-3 rounded-xl bg-white/[0.06] border border-white/10">
                        <MapPin className="w-4 h-4 text-[#A19DAB]" />
                        <input
                            type="text"
                            value={c}
                            onChange={(e) => setC(e.target.value)}
                            placeholder="Ville ou code postal"
                            className="flex-1 bg-transparent py-3 text-sm text-white placeholder:text-[#6E6A7A] outline-none"
                            data-testid="search-input-city"
                        />
                    </div>
                    <Button
                        type="submit"
                        className="h-12 px-6 bg-gradient-to-r from-[#A855F7] to-[#FF914D] hover:opacity-95 text-white font-semibold rounded-xl"
                        data-testid="search-submit-btn"
                    >
                        <Search className="w-4 h-4 mr-2" /> Rechercher
                    </Button>
                </form>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Filtres
// ─────────────────────────────────────────────────────────────────────────────
function FiltersBar({ filters, setFilters, total }) {
    const upd = (k, v) => setFilters(prev => ({ ...prev, [k]: v === '__all__' ? undefined : v }));
    return (
        <div className="sticky top-0 z-20 backdrop-blur-md bg-[#0B0512]/80 border-b border-white/10">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex flex-wrap items-center gap-2">
                <span className="text-xs text-[#A19DAB] mr-2 inline-flex items-center gap-1">
                    <Filter className="w-3.5 h-3.5" />
                    Filtres
                </span>
                <Select value={filters.type || 'all'} onValueChange={(v) => upd('type', v === 'all' ? undefined : v)}>
                    <SelectTrigger className="h-9 w-[140px] bg-white/[0.04] border-white/10 text-white text-xs" data-testid="filter-type">
                        <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        <SelectItem value="agent">Agents</SelectItem>
                        <SelectItem value="agency">Agences</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filters.specialty || '__all__'} onValueChange={(v) => upd('specialty', v)}>
                    <SelectTrigger className="h-9 w-[170px] bg-white/[0.04] border-white/10 text-white text-xs" data-testid="filter-specialty">
                        <SelectValue placeholder="Spécialité" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">Toutes spécialités</SelectItem>
                        {SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filters.property_type || '__all__'} onValueChange={(v) => upd('property_type', v)}>
                    <SelectTrigger className="h-9 w-[170px] bg-white/[0.04] border-white/10 text-white text-xs" data-testid="filter-property-type">
                        <SelectValue placeholder="Type de bien" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">Tous types</SelectItem>
                        {PROPERTY_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filters.language || '__all__'} onValueChange={(v) => upd('language', v)}>
                    <SelectTrigger className="h-9 w-[140px] bg-white/[0.04] border-white/10 text-white text-xs" data-testid="filter-language">
                        <SelectValue placeholder="Langue" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">Toutes langues</SelectItem>
                        {LANGUAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                </Select>
                <div className="ml-auto text-xs text-[#A19DAB]" data-testid="search-results-count">
                    {total != null ? <><strong className="text-white">{total}</strong> résultats</> : null}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Carte résultat
// ─────────────────────────────────────────────────────────────────────────────
function ProofChips({ proof }) {
    if (!proof) return null;
    const items = [];
    if (proof.identity) items.push({ label: 'Identité vérifiée', icon: ShieldCheck });
    if (proof.affiliation) items.push({ label: 'Affiliation vérifiée', icon: Award });
    if (!items.length) return null;
    return (
        <div className="flex flex-wrap gap-1.5 mt-3">
            {items.map(({ label, icon: Icon }) => (
                <span key={label} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-400/20 text-[10px] text-emerald-300">
                    <Icon className="w-3 h-3" />{label}
                </span>
            ))}
        </div>
    );
}

function ResultCard({ result, position, onContact }) {
    const isAgent = result.type === 'agent';
    const initials = (result.display_name || '?')
        .split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

    const handleProfileClick = () => {
        track('result_clicked', { type: result.type, slug: result.slug, position });
        track(isAgent ? 'agent_profile_viewed' : 'agency_profile_viewed',
              { slug: result.slug, source: 'search' });
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3 }}
            className="group relative rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.05] hover:border-[#A855F7]/30 transition-all p-5"
            data-testid={`result-card-${result.slug}`}
        >
            <div className="flex items-start gap-4">
                {isAgent ? (
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#681793]/40 to-[#FF914D]/30 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {result.avatar_url
                            ? <img src={result.avatar_url} alt={result.display_name} className="w-full h-full object-cover" />
                            : <span className="text-white font-bold">{initials}</span>}
                    </div>
                ) : (
                    <div className="w-14 h-14 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center flex-shrink-0">
                        {result.logo_url
                            ? <img src={result.logo_url} alt={result.display_name} className="w-full h-full object-cover rounded-xl" />
                            : <Building2 className="w-6 h-6 text-[#A855F7]" />}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Link
                            to={isAgent ? `/p/agent/${result.slug}` : `/p/agency/${result.slug}`}
                            onClick={handleProfileClick}
                            className="text-base md:text-lg font-semibold text-white hover:text-[#FF914D] truncate"
                            data-testid={`result-name-link-${result.slug}`}
                        >
                            {result.display_name}
                        </Link>
                        <Badge variant="outline" className="text-[10px] border-white/15 text-[#A19DAB]">
                            {isAgent ? <><UserRound className="w-3 h-3 mr-1" />Agent</> : <><Building2 className="w-3 h-3 mr-1" />Agence</>}
                        </Badge>
                    </div>
                    {isAgent && result.agency_name && (
                        <div className="text-xs text-[#A19DAB] mt-0.5">
                            chez <Link to={`/p/agency/${result.agency_slug}`} className="hover:text-white underline-offset-2 hover:underline">{result.agency_name}</Link>
                        </div>
                    )}
                    {result.city && (
                        <div className="text-xs text-[#A19DAB] mt-1 inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" />{result.city}
                            {result.coverage_cities?.length > 0 && (
                                <span className="text-[#6E6A7A]"> · couvre {result.coverage_cities.slice(0, 3).join(', ')}</span>
                            )}
                        </div>
                    )}

                    {/* Specialties / property types */}
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {(result.public_specialties || []).slice(0, 3).map(s => (
                            <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#A855F7]/10 border border-[#A855F7]/20 text-[10px] text-[#C7A1F0]">
                                <Award className="w-3 h-3" />{s}
                            </span>
                        ))}
                        {(result.public_property_types || []).slice(0, 2).map(s => (
                            <span key={s} className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-[10px] text-[#A19DAB]">
                                {s}
                            </span>
                        ))}
                    </div>

                    <ProofChips proof={result.proof_verifications} />

                    {/* Reasons */}
                    {result.relevance_reasons?.length > 0 && (
                        <div className="mt-3 text-xs text-[#A19DAB] italic">
                            <Sparkles className="w-3 h-3 inline mr-1 text-[#FF914D]" />
                            {result.relevance_reasons.join(' · ')}
                        </div>
                    )}
                </div>
                <div className="hidden md:flex flex-col gap-2">
                    <Link
                        to={isAgent ? `/p/agent/${result.slug}` : `/p/agency/${result.slug}`}
                        onClick={handleProfileClick}
                    >
                        <Button variant="outline" size="sm" className="border-white/20 bg-white/[0.04] text-white hover:bg-white/[0.08]" data-testid={`result-view-${result.slug}`}>
                            Voir le profil <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                    </Link>
                    <Button
                        size="sm"
                        className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] hover:opacity-95 text-white"
                        onClick={() => onContact(result)}
                        data-testid={`result-contact-${result.slug}`}
                    >
                        <Mail className="w-3 h-3 mr-1" />Contacter
                    </Button>
                </div>
            </div>
            {/* Mobile CTA */}
            <div className="md:hidden mt-3 flex gap-2">
                <Link
                    to={isAgent ? `/p/agent/${result.slug}` : `/p/agency/${result.slug}`}
                    className="flex-1"
                    onClick={handleProfileClick}
                >
                    <Button variant="outline" size="sm" className="w-full border-white/20 bg-white/[0.04] text-white">
                        Voir le profil
                    </Button>
                </Link>
                <Button
                    size="sm"
                    className="flex-1 bg-gradient-to-r from-[#A855F7] to-[#FF914D] text-white"
                    onClick={() => onContact(result)}
                >
                    Contacter
                </Button>
            </div>
        </motion.div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Contact bottom-sheet
// ─────────────────────────────────────────────────────────────────────────────
function ContactSheet({ target, onClose }) {
    const [form, setForm] = useState({
        name: '', email: '', phone: '',
        property_city: '', property_type: '',
        message: '', consent: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const open = !!target;

    const submit = async (e) => {
        e.preventDefault();
        if (!form.consent) {
            toast.error('Vous devez accepter le partage de vos coordonnées.');
            return;
        }
        setSubmitting(true);
        try {
            track('contact_submitted', { target_type: target.type, target_slug: target.slug });
            await api.post('/public/search/contact', {
                target_type: target.type,
                target_slug: target.slug,
                ...form,
                phone: form.phone || undefined,
                property_city: form.property_city || undefined,
                property_type: form.property_type || undefined,
                message: form.message || undefined,
                source: 'public_search',
            });
            toast.success('Demande envoyée — le professionnel vous contactera rapidement.');
            onClose();
        } catch (err) {
            const code = err?.response?.data?.detail || 'erreur';
            if (String(code).startsWith('identical_payload') || err?.response?.status === 429) {
                toast.error('Vous avez déjà contacté ce professionnel récemment. Patientez avant un nouvel envoi.');
            } else {
                toast.error('Envoi impossible. Réessayez dans un instant.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        if (target) track('contact_started', { target_type: target.type, target_slug: target.slug });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target?.slug]);

    return (
        <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
            <SheetContent side="right" className="bg-[#0B0512] border-white/10 text-white w-full sm:max-w-md overflow-y-auto" data-testid="contact-sheet">
                <SheetHeader>
                    <SheetTitle className="text-white" style={DISPLAY}>
                        Contacter {target?.display_name}
                    </SheetTitle>
                    <SheetDescription className="text-[#A19DAB]">
                        Présentez-vous brièvement. Le professionnel vous répondra par email.
                    </SheetDescription>
                </SheetHeader>
                <form className="mt-6 space-y-4" onSubmit={submit}>
                    <div>
                        <label className="text-xs text-[#A19DAB]">Votre nom <span className="text-red-400">*</span></label>
                        <Input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="mt-1 bg-white/[0.04] border-white/10 text-white" data-testid="contact-name" />
                    </div>
                    <div>
                        <label className="text-xs text-[#A19DAB]">Email <span className="text-red-400">*</span></label>
                        <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="mt-1 bg-white/[0.04] border-white/10 text-white" data-testid="contact-email" />
                    </div>
                    <div>
                        <label className="text-xs text-[#A19DAB]">Téléphone (optionnel)</label>
                        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            className="mt-1 bg-white/[0.04] border-white/10 text-white" data-testid="contact-phone" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-[#A19DAB]">Ville du bien</label>
                            <Input value={form.property_city} onChange={(e) => setForm({ ...form, property_city: e.target.value })}
                                className="mt-1 bg-white/[0.04] border-white/10 text-white" data-testid="contact-city" />
                        </div>
                        <div>
                            <label className="text-xs text-[#A19DAB]">Type de bien</label>
                            <Select value={form.property_type || ''} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                                <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10 text-white" data-testid="contact-ptype">
                                    <SelectValue placeholder="—" />
                                </SelectTrigger>
                                <SelectContent>
                                    {PROPERTY_TYPES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-[#A19DAB]">Message (optionnel, 800 caractères max)</label>
                        <textarea
                            value={form.message} maxLength={800}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            className="mt-1 w-full min-h-[100px] rounded-md bg-white/[0.04] border border-white/10 text-white text-sm p-2 outline-none focus:border-[#A855F7]"
                            data-testid="contact-message"
                        />
                    </div>
                    <label className="flex items-start gap-2 text-xs text-[#A19DAB]">
                        <input
                            type="checkbox"
                            checked={form.consent}
                            onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                            className="mt-0.5"
                            data-testid="contact-consent"
                        />
                        <span>
                            J'accepte que mes coordonnées soient transmises à ce professionnel
                            pour traiter ma demande (RGPD, consentement explicite).
                        </span>
                    </label>
                    <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-gradient-to-r from-[#A855F7] to-[#FF914D] text-white font-semibold"
                        data-testid="contact-submit"
                    >
                        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                        Envoyer ma demande
                    </Button>
                </form>
            </SheetContent>
        </Sheet>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// ZoneWatch — capture vendeur dans zone non couverte
// ─────────────────────────────────────────────────────────────────────────────
function ZoneWatchModal({ open, onClose, defaultCity }) {
    const [email, setEmail] = useState('');
    const [city, setCity] = useState(defaultCity || '');
    const [propertyType, setPropertyType] = useState('');
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { setCity(defaultCity || ''); }, [defaultCity]);

    const submit = async (e) => {
        e.preventDefault();
        if (!consent) { toast.error('Le consentement est requis.'); return; }
        setSubmitting(true);
        try {
            await api.post('/public/search/zone-watch', {
                email, city,
                property_type: propertyType || undefined,
                consent: true,
                source: 'public_search_empty_state',
            });
            track('zone_watch_subscribed', { zone: city });
            toast.success('C\'est noté. On vous prévient dès qu\'un pro pertinent rejoint la zone.');
            onClose();
        } catch (err) {
            if (err?.response?.status === 429) {
                toast.error('Vous avez déjà une alerte active. Patientez un peu avant un nouvel envoi.');
            } else {
                toast.error('Envoi impossible. Réessayez dans un instant.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
            <SheetContent side="right" className="bg-[#0B0512] border-white/10 text-white w-full sm:max-w-md overflow-y-auto" data-testid="zone-watch-sheet">
                <SheetHeader>
                    <SheetTitle className="text-white" style={DISPLAY}>Prévenez-moi</SheetTitle>
                    <SheetDescription className="text-[#A19DAB]">
                        Dès qu'un professionnel pertinent rejoint votre zone, nous vous écrivons.
                        Promis : aucun spam, vous pouvez vous désinscrire en un clic.
                    </SheetDescription>
                </SheetHeader>
                <form className="mt-6 space-y-4" onSubmit={submit}>
                    <div>
                        <label className="text-xs text-[#A19DAB]">Email <span className="text-red-400">*</span></label>
                        <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            className="mt-1 bg-white/[0.04] border-white/10 text-white" data-testid="zw-email" />
                    </div>
                    <div>
                        <label className="text-xs text-[#A19DAB]">Ville / zone <span className="text-red-400">*</span></label>
                        <Input required minLength={2} value={city} onChange={(e) => setCity(e.target.value)}
                            placeholder="Ex. Bordeaux 33000"
                            className="mt-1 bg-white/[0.04] border-white/10 text-white" data-testid="zw-city" />
                    </div>
                    <div>
                        <label className="text-xs text-[#A19DAB]">Type de bien (optionnel)</label>
                        <Select value={propertyType} onValueChange={setPropertyType}>
                            <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10 text-white" data-testid="zw-ptype">
                                <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                                {PROPERTY_TYPES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <label className="flex items-start gap-2 text-xs text-[#A19DAB]">
                        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                            className="mt-0.5" data-testid="zw-consent" />
                        <span>J'accepte d'être contacté(e) par email uniquement à ce sujet. Désabonnement libre.</span>
                    </label>
                    <Button type="submit" disabled={submitting}
                        className="w-full bg-gradient-to-r from-[#A855F7] to-[#FF914D] text-white font-semibold"
                        data-testid="zw-submit">
                        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        Activer l'alerte
                    </Button>
                </form>
            </SheetContent>
        </Sheet>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page racine
// ─────────────────────────────────────────────────────────────────────────────
export default function RecherchePage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [filters, setFilters] = useState(() => ({
        type: searchParams.get('type') || undefined,
        specialty: searchParams.get('specialty') || undefined,
        property_type: searchParams.get('property_type') || undefined,
        language: searchParams.get('language') || undefined,
        city: searchParams.get('city') || undefined,
    }));
    const [q, setQ] = useState(searchParams.get('q') || '');
    const [page, setPage] = useState(Number(searchParams.get('page') || 1));
    const [results, setResults] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [contactTarget, setContactTarget] = useState(null);
    const [zoneWatchOpen, setZoneWatchOpen] = useState(false);
    const firstLoadRef = useRef(true);

    // Sync URL ↔ state (préserve les query params inconnus comme `source`)
    useEffect(() => {
        const sp = new URLSearchParams(searchParams);
        ['q', 'type', 'city', 'specialty', 'property_type', 'language', 'page'].forEach(k => sp.delete(k));
        if (q) sp.set('q', q);
        if (filters.type) sp.set('type', filters.type);
        if (filters.city) sp.set('city', filters.city);
        if (filters.specialty) sp.set('specialty', filters.specialty);
        if (filters.property_type) sp.set('property_type', filters.property_type);
        if (filters.language) sp.set('language', filters.language);
        if (page > 1) sp.set('page', String(page));
        setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, filters, page]);

    // Fetch
    const runSearch = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const body = {
                q: q || undefined,
                filters: {
                    type: filters.type || 'all',
                    city: filters.city,
                    specialty: filters.specialty,
                    property_type: filters.property_type,
                    language: filters.language,
                },
                page, page_size: PAGE_SIZE,
            };
            const { data } = await api.post('/public/search', body);
            setResults(data.results || []);
            setMeta(data.meta || null);
            track('search_submitted', {
                has_query: !!q,
                has_filters: Object.values(filters).some(Boolean),
                results_count: data.meta?.total || 0,
            });
            if ((data.meta?.total || 0) === 0) {
                track('search_zero_results', { query: q || '', filters });
            }
        } catch (err) {
            setError(err?.response?.data?.detail || 'Recherche indisponible');
            setResults([]); setMeta(null);
        } finally {
            setLoading(false);
        }
    }, [q, filters, page]);

    // Initial + on filter change
    useEffect(() => {
        if (firstLoadRef.current) {
            track('search_opened', { source: searchParams.get('source') || 'direct' });
            firstLoadRef.current = false;
        }
        runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.type, filters.specialty, filters.property_type, filters.language, filters.city, page]);

    const onHeroSubmit = ({ q: newQ, city: newCity }) => {
        setQ(newQ); setFilters(f => ({ ...f, city: newCity || undefined })); setPage(1);
        setTimeout(runSearch, 0);
    };

    const totalPages = meta ? Math.max(1, Math.ceil(meta.total / PAGE_SIZE)) : 1;

    return (
        <div className="min-h-screen bg-[#0B0512] text-white">
            <SeoHead hasParams={!!searchParams.toString()} />

            {/* Top nav minimaliste */}
            <nav className="border-b border-white/10 bg-[#0B0512]/80 backdrop-blur-md sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 md:px-12 h-14 flex items-center justify-between">
                    <Link to="/" className="font-black text-xl flex items-center gap-0.5" data-testid="nav-home">
                        <span className="bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent">Homly</span>
                        <span className="text-[#FF914D]">.</span>
                    </Link>
                    <div className="flex items-center gap-3 text-xs">
                        <Link to="/pour-les-agents" className="text-[#A19DAB] hover:text-white hidden sm:inline">Vous êtes agent ?</Link>
                        <Link to="/login">
                            <Button variant="outline" size="sm" className="border-white/20 bg-transparent text-white hover:bg-white/[0.06]" data-testid="nav-login">
                                Espace pro
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            <Hero query={q} city={filters.city} onSubmit={onHeroSubmit} />

            <FiltersBar filters={filters} setFilters={(updater) => { setFilters(updater); setPage(1); }} total={meta?.total} />

            <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
                {error && (
                    <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300 mb-6" data-testid="search-error">
                        Une erreur est survenue : {error}. Réessayez dans un instant.
                    </div>
                )}

                {loading && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="search-skeleton">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Skeleton key={i} className="h-32 rounded-2xl bg-white/[0.04]" />
                        ))}
                    </div>
                )}

                {!loading && !error && results.length === 0 && (
                    <div className="text-center py-16" data-testid="search-empty">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/[0.04] border border-white/10 mb-4">
                            <Search className="w-7 h-7 text-[#A19DAB]" />
                        </div>
                        <h3 className="text-xl font-semibold text-white" style={DISPLAY}>
                            Aucun résultat dans cette zone pour ces critères.
                        </h3>
                        <p className="mt-2 text-sm text-[#A19DAB] max-w-md mx-auto">
                            Essayez d'élargir la zone, retirez un filtre, ou laissez-nous votre demande —
                            nous vous prévenons dès qu'un pro pertinent rejoint le réseau.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3 justify-center">
                            <Button
                                className="bg-white/[0.06] hover:bg-white/[0.1] text-white border border-white/15"
                                onClick={() => { setFilters({ type: undefined }); setQ(''); setPage(1); }}
                                data-testid="search-reset"
                            >
                                <X className="w-4 h-4 mr-2" />Réinitialiser les filtres
                            </Button>
                            <Button
                                className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] text-white font-semibold"
                                onClick={() => setZoneWatchOpen(true)}
                                data-testid="search-zonewatch-btn"
                            >
                                <Sparkles className="w-4 h-4 mr-2" />Prévenez-moi
                            </Button>
                        </div>
                    </div>
                )}

                {!loading && !error && results.length > 0 && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="search-results">
                            {results.map((r, i) => (
                                <ResultCard
                                    key={`${r.type}-${r.slug}`}
                                    result={r}
                                    position={(page - 1) * PAGE_SIZE + i + 1}
                                    onContact={setContactTarget}
                                />
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="mt-10 flex items-center justify-center gap-2" data-testid="search-pagination">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={page <= 1}
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    className="border-white/15 bg-white/[0.04] text-white disabled:opacity-30"
                                    data-testid="pagination-prev"
                                >
                                    Précédent
                                </Button>
                                <span className="text-xs text-[#A19DAB] px-3">
                                    Page <strong className="text-white">{page}</strong> / {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(p => p + 1)}
                                    className="border-white/15 bg-white/[0.04] text-white disabled:opacity-30"
                                    data-testid="pagination-next"
                                >
                                    Suivant
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* Footer transparence */}
            <footer className="border-t border-white/10 mt-12">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 text-xs text-[#A19DAB]">
                    <div className="flex flex-wrap justify-between gap-3">
                        <div>
                            Classement transparent · Pas de pub · Pas de rang payant ·
                            <Link to="/cgu" className="ml-1 hover:text-white">Mentions</Link>
                        </div>
                        <div className="flex gap-3">
                            <Link to="/" className="hover:text-white">Accueil</Link>
                            <Link to="/pour-les-agents" className="hover:text-white">Pour les agents</Link>
                            <Link to="/pour-les-agences" className="hover:text-white">Pour les agences</Link>
                        </div>
                    </div>
                </div>
            </footer>

            <ContactSheet target={contactTarget} onClose={() => setContactTarget(null)} />
            <ZoneWatchModal open={zoneWatchOpen} onClose={() => setZoneWatchOpen(false)} defaultCity={filters.city} />
        </div>
    );
}
